import React, { useState, useEffect } from 'react'
import { RegistrationRequest, WalletType } from '../types/api'
import { UserPoll } from '../types'
import { walletService, userPollService } from '../services/api'
import UserPollSection from './UserPollSection'
import './RegistrationForm.css'

const RegistrationForm: React.FC = () => {
  const detectDeviceWallet = (): WalletType | null => {
    const userAgent = navigator.userAgent.toLowerCase()
    if (
      userAgent.includes('iphone') ||
      userAgent.includes('ipad') ||
      userAgent.includes('mac')
    ) {
      return 'apple'
    } else if (userAgent.includes('android')) {
      return 'google'
    }
    return null
  }
  // Function to get email from URL parameters
  const getEmailFromUrl = (): string => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('email') || ''
  }

  const [formData, setFormData] = useState<RegistrationRequest>({
    name: '',
    email: getEmailFromUrl(),
    birthdate: '',
  })
  const [walletType, setWalletType] = useState<WalletType | null>(
    detectDeviceWallet(),
  )
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)

  // Check if email is locked (came from URL)
  const isEmailLocked = Boolean(getEmailFromUrl())

  const [userPolls, setUserPolls] = useState<UserPoll[]>([])
  const [pollAnswers, setPollAnswers] = useState<Record<number, number>>({})
  const [isLoadingPolls, setIsLoadingPolls] = useState<boolean>(false)
  const [showPollError, setShowPollError] = useState<boolean>(false)

  useEffect(() => {
    const loadUserPolls = async () => {
      setIsLoadingPolls(true)
      try {
        const response = await userPollService.getUserPolls()
        // Filter only active polls and sort by order
        const activePolls = response.userPolls
          .filter((poll) => poll.active)
          .sort((a, b) => a.order - b.order)
        setUserPolls(activePolls)
      } catch (err) {
        console.error('Error loading user polls:', err)
      } finally {
        setIsLoadingPolls(false)
      }
    }

    loadUserPolls()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!walletType) {
      setError('Por favor, selecciona el tipo de wallet')
      return
    }

    // Validate that all user poll questions are answered
    if (userPolls.length > 0) {
      const unansweredQuestions = userPolls.filter(
        (poll) => !pollAnswers[poll.id],
      )
      if (unansweredQuestions.length > 0) {
        setShowPollError(true)
        return
      }
    }

    // Clear poll error if all questions are answered
    setShowPollError(false)

    setIsLoading(true)

    try {
      // Prepare user poll answers for submission
      const userPollAnswers = Object.entries(pollAnswers)
        .filter(([_, answerId]) => answerId > 0)
        .map(([pollId, answerId]) => ({
          userPollId: Number(pollId),
          userPollDetailId: answerId,
        }))

      const registrationPayload = {
        ...formData,
        userPollAnswers:
          userPollAnswers.length > 0 ? userPollAnswers : undefined,
      }

      await walletService.registerPass(registrationPayload, walletType)
      setSuccess(true)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Ops, algo falló. Intente nuevamente.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    // Prevent changing email if it's locked
    if (name === 'email' && isEmailLocked) {
      return
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleWalletTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = e.target.value as WalletType
    setWalletType(selectedType)
  }

  const handlePollAnswerChange = (pollId: number, answerId: number) => {
    setPollAnswers((prev: Record<number, number>) => ({
      ...prev,
      [pollId]: answerId,
    }))

    // Clear poll error visual when user answers a question
    if (showPollError) {
      setShowPollError(false)
    }
  }

  const brands: Record<
    string,
    {
      className: string
      appName: string
      logo: string
    }
  > = {
    paru: {
      className: 'app-paru',
      appName: 'Páru Members',
      logo: '/logo-paru.png',
    },
    jb: {
      className: 'app-jb',
      appName: 'Jeremy Button',
      logo: '/logo-jb.png',
    },
    cs: {
      className: 'app-cs',
      appName: 'Casa Saenz',
      logo: '/logo.webp',
    },
  }

  const app = process.env.REACT_APP_NAME?.toLowerCase()
  const brand = app && brands[app] ? brands[app] : null
  const appName = brand ? brand.appName : 'App'
  const appClass = brand ? brand.className : ''

  return (
    <div className={`container ${appClass}`}>
      <h1>{appName}</h1>
      <h2>Registrate y obtené tu tarjeta</h2>

      <form onSubmit={handleSubmit}>
        <input
          type="text"
          id="name"
          name="name"
          placeholder="Nombre"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
        <input
          type="email"
          id="email"
          name="email"
          placeholder="Correo electrónico"
          value={formData.email}
          onChange={handleInputChange}
          required
          disabled={isEmailLocked}
          className={isEmailLocked ? 'email-locked' : ''}
        />
        <div className="form-group">
          <label htmlFor="birthdate">Fecha de nacimiento</label>
          <input
            type="date"
            id="birthdate"
            name="birthdate"
            value={formData.birthdate}
            onChange={handleInputChange}
            onInvalid={(e) =>
              e.currentTarget.setCustomValidity(
                'Debes ingresar tu fecha de nacimiento',
              )
            }
            onInput={(e) => e.currentTarget.setCustomValidity('')}
            required
            className="date-input"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
        <select
          id="walletType"
          name="walletType"
          value={walletType || ''}
          onChange={handleWalletTypeChange}
          required
        >
          <option value="" disabled>
            Seleccionar tipo de wallet
          </option>
          <option value="apple">Apple Wallet</option>
          <option value="google">Google Wallet</option>
        </select>

        {/* User Polls Section */}
        {!isLoadingPolls && userPolls.length > 0 && (
          <UserPollSection
            userPolls={userPolls}
            pollAnswers={pollAnswers}
            isLoadingPolls={isLoadingPolls}
            showPollError={showPollError}
            onPollAnswerChange={handlePollAnswerChange}
          />
        )}

        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Registrando...' : 'Registrarse'}
        </button>
      </form>
      {isLoading && <div id="spinner" />}
      {error && <div className="error">{error}</div>}
      {success && (
        <div className="success">
          {
            'Todo listo! El último paso es revisar tu mail para descargar tu e-card. Muchas gracias!'
          }
        </div>
      )}
    </div>
  )
}

export default RegistrationForm
